/**
 * ClusterMarker — Aggregierter Annotations-Marker für die Übersichts-Stufe
 *
 * Zeigt die Anzahl der Annotationen im Cluster + dominante Typ-Farbe.
 * Hover zeigt Typ-Aufschlüsselung, Klick zoomt auf die Cluster-Ausdehnung.
 */

import React, { useState, useCallback } from 'react'
import {
  Annotation,
  ANNOTATION_TYPE_COLORS,
  ANNOTATION_TYPE_LABELS,
} from '../../types/annotations'
import { C } from '../../styles/theme'

export interface AnnotationCluster {
  id: string
  annotations: Annotation[]
  centerScreen: { x: number; y: number }
  dominantType: string
  dominantColor: string
}

interface ClusterMarkerProps {
  cluster: AnnotationCluster
  viewerRef: React.RefObject<any>
}

export function ClusterMarker({ cluster, viewerRef }: ClusterMarkerProps) {
  const [hovered, setHovered] = useState(false)
  const count = cluster.annotations.length
  const color = cluster.dominantColor

  // Typ-Aufschlüsselung
  const typeBreakdown = React.useMemo(() => {
    const counts: Record<string, number> = {}
    for (const ann of cluster.annotations) {
      counts[ann.type] = (counts[ann.type] || 0) + 1
    }
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([type, cnt]) => ({
        type,
        count: cnt,
        label: ANNOTATION_TYPE_LABELS[type as keyof typeof ANNOTATION_TYPE_LABELS] ?? type,
        color: ANNOTATION_TYPE_COLORS[type as keyof typeof ANNOTATION_TYPE_COLORS] ?? '#999',
      }))
  }, [cluster.annotations])

  // Klick: Kamera auf die Cluster-Ausdehnung zoomen
  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    const viewer = viewerRef.current
    const THREE = (window as any).Potree?.THREE
    if (!viewer || !THREE) return

    const box = new THREE.Box3()
    for (const ann of cluster.annotations) {
      box.expandByPoint(new THREE.Vector3(ann.position.x, ann.position.y, ann.position.z))
    }

    const center = new THREE.Vector3()
    box.getCenter(center)
    const size = new THREE.Vector3()
    box.getSize(size)
    const maxDim = Math.max(size.x, size.y, size.z, 2)

    viewer.scene.view.position.set(center.x, center.y, center.z)
    viewer.scene.view.radius = maxDim * 2.5
  }, [cluster.annotations, viewerRef])

  // Dynamische Größe basierend auf Anzahl (px-Wert für Canvas-Positionierung)
  const markerSizePx = Math.min(48, 24 + count * 2)
  // em-Äquivalent für CSS (relativ zu 16px-Basis)
  const markerSizeEm = `${markerSizePx / 16}em`

  return (
    <div
      data-component="ClusterMarker"
      data-cluster-count={count}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        cursor: 'pointer',
        zIndex: hovered ? 10300 : 10050,
        pointerEvents: 'auto',
        userSelect: 'none',
      }}
    >
      {/* Cluster-Kreis */}
      <div style={{
        width: markerSizeEm,
        height: markerSizeEm,
        borderRadius: '50%',
        background: `${color}28`,
        border: `2px solid ${color}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: `0 0 0 0.1875em ${color}15, 0 0.125em 0.5em rgba(0,0,0,0.2)`,
        /* 3px/16px, 2px/16px, 8px/16px */
        transition: 'all 0.2s ease',
        transform: hovered ? 'scale(1.15)' : 'scale(1)',
      }}>
        <span style={{
          color: color,
          fontSize: count >= 100 ? '0.625em' : count >= 10 ? '0.75em' : '0.875em',
          /* 10px/16px, 12px/16px, 14px/16px */
          fontWeight: 700,
          fontFamily: C.fontSans,
          lineHeight: 1,
        }}>
          {count}
        </span>
      </div>

      {/* Hover-Tooltip: Typ-Aufschlüsselung */}
      {hovered && (
        <div style={{
          position: 'absolute',
          bottom: `${(markerSizePx + 8) / 16}em`, /* (markerSize + 8px) / 16px */
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#ffffff',
          border: `1px solid ${C.border}`,
          borderRadius: '0.5em',            /* 8px at 16px */
          boxShadow: '0 0.25em 1em rgba(0,0,0,0.12)', /* 4px/16px, 16px/16px */
          padding: '0.5em 0.75em',          /* 8px/16px, 12px/16px */
          minWidth: '8.75em',              /* 140px at 16px */
          whiteSpace: 'nowrap',
          fontFamily: C.fontSans,
          zIndex: 10400,
        }}>
          <div style={{
            fontSize: '0.625em',           /* 10px at 16px */
            fontWeight: 600,
            color: C.textMuted,
            marginBottom: '0.6em',         /* 6px/10px */
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}>
            {count} Annotationen
          </div>
          {typeBreakdown.map(tb => (
            <div key={tb.type} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.375em',               /* 6px at 16px */
              padding: '0.125em 0',         /* 2px at 16px */
            }}>
              <div style={{
                width: '0.375em',           /* 6px at 16px */
                height: '0.375em',
                borderRadius: '50%',
                background: tb.color,
                flexShrink: 0,
              }} />
              <span style={{ fontSize: '0.6875em', color: C.textSecondary, flex: 1 }}>
                {/* 11px at 16px */}
                {tb.label}
              </span>
              <span style={{ fontSize: '0.6875em', color: C.textDim, fontWeight: 600 }}>
                {tb.count}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
